import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { CreditCard, Search, ChevronDown, RefreshCw, Plus, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  active: 'bg-emerald-500/20 text-emerald-400',
  pending: 'bg-amber-500/20 text-amber-400',
  cancelled: 'bg-red-500/20 text-red-400',
  suspended: 'bg-orange-500/20 text-orange-400',
  frozen: 'bg-cyan-500/20 text-cyan-400',
  terminated: 'bg-gray-500/20 text-gray-400',
};

const AdminSubscriptions = () => {
  const { token } = useAuth();
  const [subs, setSubs] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ member_id: '', plan_name: 'Standard Membership', monthly_amount: '29.99', start_date: new Date().toISOString().split('T')[0] });
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchSubs = useCallback(async () => {
    const p = new URLSearchParams({ page: String(page), page_size: '50' });
    if (statusFilter) p.append('status', statusFilter);
    const res = await fetch(`${API_URL}/api/subscriptions?${p}`, { headers });
    if (res.ok) {
      const d = await res.json();
      let items = d.items || [];
      if (search) {
        const q = search.toLowerCase();
        items = items.filter(s => s.member_name?.toLowerCase().includes(q) || s.member_email?.toLowerCase().includes(q) || s.member_number?.toLowerCase().includes(q));
      }
      setSubs(items);
      setTotal(d.total || 0);
    }
  }, [page, statusFilter, search]);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const searchMembers = async (q) => {
    setMemberSearch(q);
    if (q.length < 2) { setMembers([]); return; }
    try {
      const res = await fetch(`${API_URL}/api/members?search=${encodeURIComponent(q)}&page_size=10`, { headers });
      if (res.ok) { const d = await res.json(); setMembers(d.items || d || []); }
    } catch (e) {}
  };

  const createSubscription = async () => {
    if (!createForm.member_id) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/subscriptions`, {
        method: 'POST', headers,
        body: JSON.stringify({
          member_id: createForm.member_id,
          plan_name: createForm.plan_name,
          monthly_amount: parseFloat(createForm.monthly_amount),
          start_date: createForm.start_date,
          payment_method: 'manual'
        })
      });
      if (res.ok) {
        setShowCreate(false);
        setCreateForm({ member_id: '', plan_name: 'Standard Membership', monthly_amount: '29.99', start_date: new Date().toISOString().split('T')[0] });
        fetchSubs();
      }
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  const stats = {
    active: subs.filter(s => s.status === 'active').length,
    pending: subs.filter(s => s.status === 'pending').length,
    cancelled: subs.filter(s => s.status === 'cancelled').length,
    frozen: subs.filter(s => s.status === 'frozen').length,
  };

  return (
    <div className="p-4 lg:p-8" data-testid="admin-subscriptions">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-uf-text flex items-center gap-2"><CreditCard className="w-6 h-6 text-uf-red" />Subscriptions</h1>
          <p className="text-uf-text-muted text-sm mt-1">{total} total subscriptions</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 bg-uf-red text-white rounded-lg px-4 py-2 text-sm hover:bg-uf-red-hover" data-testid="create-subscription-btn">
            <Plus className="w-4 h-4" />New Subscription
          </button>
          <button onClick={fetchSubs} className="flex items-center gap-2 bg-uf-surface border border-uf-border rounded-lg px-4 py-2 text-uf-text text-sm hover:bg-uf-surface-hi" data-testid="refresh-subs">
            <RefreshCw className="w-4 h-4" />Refresh
          </button>
        </div>
      </div>

      {/* Create Subscription Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="create-subscription-modal">
          <div className="bg-uf-surface border border-uf-border rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-uf-border">
              <h3 className="text-lg font-bold text-uf-text">Create Manual Subscription</h3>
              <button onClick={() => setShowCreate(false)} className="text-uf-text-muted"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs text-uf-text-muted uppercase mb-1 block">Member</label>
                <input type="text" placeholder="Search member by name or email..." value={memberSearch}
                  onChange={e => searchMembers(e.target.value)}
                  className="w-full bg-uf-black border border-uf-border rounded-lg px-3 py-2 text-sm text-uf-text" data-testid="member-search-input" />
                {members.length > 0 && (
                  <div className="mt-1 bg-uf-black border border-uf-border rounded-lg max-h-32 overflow-y-auto">
                    {members.map(m => (
                      <button key={m.id} onClick={() => { setCreateForm({...createForm, member_id: m.id}); setMemberSearch(`${m.first_name} ${m.last_name} (${m.email})`); setMembers([]); }}
                        className="w-full px-3 py-2 text-left text-sm text-uf-text hover:bg-uf-surface-hi">
                        {m.first_name} {m.last_name} - {m.email}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs text-uf-text-muted uppercase mb-1 block">Plan Name</label>
                <select value={createForm.plan_name} onChange={e => setCreateForm({...createForm, plan_name: e.target.value})}
                  className="w-full bg-uf-black border border-uf-border rounded-lg px-3 py-2 text-sm text-uf-text">
                  <option>Standard Membership</option><option>Premium Membership</option><option>Student Membership</option><option>Off-Peak Membership</option><option>Day Pass</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-uf-text-muted uppercase mb-1 block">Monthly Amount</label>
                  <input type="number" step="0.01" value={createForm.monthly_amount} onChange={e => setCreateForm({...createForm, monthly_amount: e.target.value})}
                    className="w-full bg-uf-black border border-uf-border rounded-lg px-3 py-2 text-sm text-uf-text" />
                </div>
                <div>
                  <label className="text-xs text-uf-text-muted uppercase mb-1 block">Start Date</label>
                  <input type="date" value={createForm.start_date} onChange={e => setCreateForm({...createForm, start_date: e.target.value})}
                    className="w-full bg-uf-black border border-uf-border rounded-lg px-3 py-2 text-sm text-uf-text" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-uf-border">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-uf-text-muted text-sm">Cancel</button>
              <button onClick={createSubscription} disabled={!createForm.member_id || creating}
                className="px-4 py-2 bg-uf-red text-white rounded-lg text-sm disabled:opacity-50" data-testid="save-subscription-btn">
                {creating ? 'Creating...' : 'Create Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Active', value: stats.active, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
          { label: 'Pending', value: stats.pending, color: 'text-amber-400', bg: 'bg-amber-500/10' },
          { label: 'Cancelled', value: stats.cancelled, color: 'text-red-400', bg: 'bg-red-500/10' },
          { label: 'Frozen', value: stats.frozen, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4 cursor-pointer hover:ring-1 ring-current transition-all`} onClick={() => setStatusFilter(s.label.toLowerCase() === statusFilter ? '' : s.label.toLowerCase())} data-testid={`sub-stat-${s.label.toLowerCase()}`}>
            <p className="text-uf-text-muted text-xs uppercase tracking-wider">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color} mt-1`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-uf-text-muted" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or member number..."
            style={{ background: 'var(--uf-surface)', border: '1px solid var(--uf-border)', borderRadius: '0.5rem', padding: '0.5rem 0.75rem 0.5rem 2.5rem' }}
            className="w-full text-uf-text text-sm placeholder:text-uf-text-muted" data-testid="sub-search" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ width: 'auto', minWidth: '150px' }}
          className="bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="sub-status-filter">
          <option value="">All Statuses</option>
          {['active', 'pending', 'cancelled', 'suspended', 'frozen', 'terminated'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-uf-border text-left">
              <th className="p-3 text-uf-text-muted font-medium">Member</th>
              <th className="p-3 text-uf-text-muted font-medium">Plan</th>
              <th className="p-3 text-uf-text-muted font-medium">Status</th>
              <th className="p-3 text-uf-text-muted font-medium">Amount</th>
              <th className="p-3 text-uf-text-muted font-medium">Start</th>
              <th className="p-3 text-uf-text-muted font-medium">Next Billing</th>
            </tr></thead>
            <tbody>
              {subs.map(s => (
                <tr key={s.id} className="border-b border-uf-border/50 hover:bg-uf-surface-hi" data-testid={`sub-row-${s.id}`}>
                  <td className="p-3">
                    <p className="text-uf-text font-medium">{s.member_name}</p>
                    <p className="text-uf-text-muted text-xs">{s.member_email}</p>
                  </td>
                  <td className="p-3 text-uf-text">{s.plan_name}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs capitalize ${statusColors[s.status] || 'bg-gray-500/20 text-gray-400'}`}>{s.status}</span></td>
                  <td className="p-3 text-uf-text font-medium">{s.monthly_amount > 0 ? `£${s.monthly_amount.toFixed(2)}/mo` : '—'}</td>
                  <td className="p-3 text-uf-text-muted">{s.start_date || '—'}</td>
                  <td className="p-3 text-uf-text-muted">{s.next_billing_date || '—'}</td>
                </tr>
              ))}
              {!subs.length && <tr><td colSpan={6} className="p-8 text-center text-uf-text-muted">No subscriptions found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {total > 50 && (
        <div className="flex justify-center gap-2 mt-4">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 bg-uf-surface border border-uf-border rounded text-uf-text text-sm disabled:opacity-50">Prev</button>
          <span className="px-3 py-1.5 text-uf-text-muted text-sm">Page {page}</span>
          <button onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 bg-uf-surface border border-uf-border rounded text-uf-text text-sm">Next</button>
        </div>
      )}
    </div>
  );
};

export default AdminSubscriptions;
